import { CLEAR_CART, FETCH_ERROR, GET_CART } from "../../constants/ActionTypes";
import { getShippingRates, updateCheckout } from "./Checkout";
import { fetchStart, fetchSuccess } from "./Common";
import CartEventTracker from "../../utils/events";
import {getCurrentLanguageCode, getShopifyLanguageCode} from "../../utils/helpers";

const cartTracker = new CartEventTracker();

/**
 * get shopping cart details
 * @param {Array.<object>} cart - shopping cart details
 * @returns
 */
export const getCart = cart => {
  return {
    type: GET_CART,
    payload: cart,
  };
};

/**
 * Update line item (migrated to Storefront API)
 *
 * @param {object} item - line item
 * @returns
 */
export const updateLineItem = item => {
  return (dispatch, getState) => {
    const cartId = getState().checkout.checkoutId;
    const client = getState().shopify?.client;

    if (!client || !cartId) {
      dispatch({ type: FETCH_ERROR, payload: "Cart not initialized" });
      return;
    }

    dispatch(fetchStart());

    const cartLinesUpdateMutation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) @inContext(language: ${getShopifyLanguageCode(getCurrentLanguageCode())}) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
            totalQuantity
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      product {
                        id
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                              altText
                            }
                          }
                        }
                      }
                    }
                  }
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
            buyerIdentity {
              countryCode
              customer {
                id
                email
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const cartLines = Array.isArray(item)
      ? item.map(lineItem => ({
          id: lineItem.id,
          quantity: lineItem.quantity,
        }))
      : [
          {
            id: item.id,
            quantity: item.quantity,
          },
        ];

    client
      .fetch(cartLinesUpdateMutation, {
        variables: {
          cartId: cartId,
          lines: cartLines,
        },
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }
      })
      .then(({ data, errors }) => {
        if (errors && errors.length > 0) {
          dispatch({ type: FETCH_ERROR, payload: "Failed to update cart item" });
          return;
        }

        if (data?.cartLinesUpdate?.userErrors?.length > 0) {
          dispatch({ type: FETCH_ERROR, payload: data.cartLinesUpdate.userErrors[0].message });
          return;
        }

        const cart = data?.cartLinesUpdate?.cart;

        const itemDetails = cart?.lines?.edges?.find(edge =>
          cartLines.some(line => edge.node.id === line.id)
        )?.node;

        if (itemDetails) {
          cartTracker.addToCartEvent(itemDetails);
        }

        dispatch(fetchSuccess());
        dispatch(getShippingRates());
        dispatch(updateCheckout(cart));
      })
      .catch(err => {
        dispatch({ type: FETCH_ERROR, payload: err.message });
      });
  };
};

/**
 * Clear the shopping cart
 *
 * @returns
 */
export const clearCart = () => {
  return {
    type: CLEAR_CART,
  };
};
